// Sell signal for an existing property. It reads the same market-timing data as
// the buy signals, inverted: a segment trading rich vs its own recent trend
// (i.e. expensive to buy, low entry-score) is a favourable time to SELL.

import type { SeriesSignal } from "./timing";
import { TIMING_RULE, familyOf, timingInfluences } from "./timingRule";

export interface SellSignal {
  sellScore: number; // 0..100, higher = more favourable to sell
  verdict: "favourable" | "neutral" | "hold";
  momentum12m: number | null;
  valuationZ: number | null;
  latest: number | null;
  metric: string;
  note: string;
}

// Segments a user can tag their current property as, mapped to market series.
export const SELL_SEGMENTS: { id: string; label: string }[] = [
  { id: "hdb.resale.2-room", label: "HDB 2-Room" },
  { id: "hdb.resale.3-room", label: "HDB 3-Room" },
  { id: "hdb.resale.4-room", label: "HDB 4-Room" },
  { id: "hdb.resale.5-room", label: "HDB 5-Room" },
  { id: "hdb.resale.executive", label: "HDB Executive" },
  { id: "private.ppi.ccr", label: "Private — CCR (Core Central)" },
  { id: "private.ppi.rcr", label: "Private — RCR (Rest of Central)" },
  { id: "private.ppi.ocr", label: "Private — OCR (Outside Central)" },
  { id: "private.ppi.all", label: "Private — overall" },
];

export function computeSellSignal(sig: SeriesSignal | undefined): SellSignal | null {
  if (!sig || sig.entry_score == null) return null;
  const sellScore = 100 - sig.entry_score; // inverse of the buy entry-score
  // Backtest rule: where the score isn't predictive (HDB), it never says sell/hold.
  const descriptiveOnly = !timingInfluences(sig.id);
  const verdict: SellSignal["verdict"] = descriptiveOnly
    ? "neutral"
    : sellScore >= 62 ? "favourable" : sellScore <= 40 ? "hold" : "neutral";

  const note = descriptiveOnly
    ? `${TIMING_RULE[familyOf(sig.id)].evidence} Sell when it suits your plans; timing isn't a reliable reason here.`
    : verdict === "favourable"
      ? "Your segment is trading rich versus its recent trend — a favourable window to sell into strength."
      : verdict === "hold"
        ? "Prices look soft / below trend — holding may capture further recovery; not an ideal time to sell."
        : "Neither hot nor soft for your segment — sell when it suits your plans rather than for timing.";

  return {
    sellScore,
    verdict,
    momentum12m: sig.momentum_12m,
    valuationZ: sig.valuation_z,
    latest: sig.latest_value,
    metric: sig.metric,
    note,
  };
}
